import { DOCUMENT, ViewportScroller } from '@angular/common';
import { Inject } from '@angular/core';

export class CmViewportScroller implements ViewportScroller {
	private offset: () => [number, number] = () => [0, 0];

	constructor(
		private readonly scrollElementSelector: string,
		@Inject(DOCUMENT) private readonly document: Document,
		private readonly window: Window
	) {}

	/**
	 * Configures the top offset used when scrolling to an anchor.
	 * @param offset A position in screen coordinates (a tuple with x and y values)
	 * or a function that returns the top offset position.
	 *
	 */
	setOffset(offset: [number, number] | (() => [number, number])): void {
		if (Array.isArray(offset)) {
			this.offset = () => offset;
		} else {
			this.offset = offset;
		}
	}

	/**
	 * Retrieves the current scroll position.
	 * @returns The position in screen coordinates.
	 */
	getScrollPosition(): [number, number] {
		const scrollEl = this.document.querySelector(this.scrollElementSelector);
		if (this.supportScrollRestoration() && scrollEl) {
			return [scrollEl.scrollLeft, scrollEl.scrollTop];
		} else {
			return [0, 0];
		}
	}

	/**
	 * Sets the scroll position.
	 * @param position The new position in screen coordinates.
	 */
	scrollToPosition(position: [number, number]): void {
		const scrollEl = this.document.querySelector(this.scrollElementSelector);
		if (this.supportScrollRestoration() && scrollEl) {
			scrollEl.scrollTo({ left: position[0], top: position[1] });
		}
	}

	/**
	 * Scrolls to an element and attempts to focus the element.
	 *
	 * Note that the function name here is misleading in that the target string may be an ID for a
	 * non-anchor element.
	 *
	 * @param target The ID of an element or name of the anchor.
	 *
	 * @see https://html.spec.whatwg.org/#the-indicated-part-of-the-document
	 * @see https://html.spec.whatwg.org/#scroll-to-fragid
	 */
	scrollToAnchor(target: string): void {
		if (!this.supportsScrolling()) {
			return;
		}

		// This is needed for elements to be loaded on the screen. The whole function could be spotty
		// because it depends on how quickly the elements render. We are not using it yet because anchorScrolling
		// is disabled by default and we have not enabled it.
		setTimeout(() => {
			const elSelected = this.findAnchorFromDocument(this.document, target);

			if (elSelected) {
				this.scrollToElement(elSelected);
				// After scrolling to the element, the spec dictates that we follow the focus steps for the
				// target. Rather than following the robust steps, simply attempt focus.
				//
				// @see https://html.spec.whatwg.org/#get-the-focusable-area
				// @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLOrForeignElement/focus
				// @see https://html.spec.whatwg.org/#focusable-area
				elSelected.focus();
			}
		}, 300);
	}

	/**
	 * Disables automatic scroll restoration provided by the browser.
	 */
	setHistoryScrollRestoration(scrollRestoration: 'auto' | 'manual'): void {
		if (this.supportScrollRestoration()) {
			const history = this.window.history;
			if (history && history.scrollRestoration) {
				history.scrollRestoration = scrollRestoration;
			}
		}
	}

	/**
	 * Scrolls to an element using the native offset and the specified offset set on this scroller.
	 *
	 * The offset can be used when we know that there is a floating header and scrolling naively to an
	 * element (ex: `scrollIntoView`) leaves the element hidden behind the floating header.
	 */
	private scrollToElement(el: HTMLElement): void {
		const rect = el.getBoundingClientRect();
		const scrollEl = this.document.querySelector(this.scrollElementSelector);
		const left = rect.left + scrollEl.scrollLeft;
		const top = rect.top + scrollEl.scrollTop;
		const offset = this.offset();
		scrollEl.scrollTo({ left: left - offset[0], top: top - offset[1] });
	}

	private supportScrollRestoration(): boolean {
		try {
			if (!this.supportsScrolling()) {
				return false;
			}
			// The `scrollRestoration` property could be on the `history` instance or its prototype.
			const scrollRestorationDescriptor =
				this.getScrollRestorationProperty(this.window.history) ||
				this.getScrollRestorationProperty(Object.getPrototypeOf(this.window.history));
			// We can write to the `scrollRestoration` property if it is a writable data field or it has a
			// setter function.
			return (
				!!scrollRestorationDescriptor &&
				!!(scrollRestorationDescriptor.writable || scrollRestorationDescriptor.set)
			);
		} catch {
			return false;
		}
	}

	private supportsScrolling(): boolean {
		try {
			return !!this.window && !!this.window.scrollTo && 'pageXOffset' in this.window;
		} catch {
			return false;
		}
	}

	private getScrollRestorationProperty(obj: any): PropertyDescriptor | undefined {
		return Object.getOwnPropertyDescriptor(obj, 'scrollRestoration');
	}

	private findAnchorFromDocument(document: Document, target: string): HTMLElement | null {
		const documentResult = document.getElementById(target) || document.getElementsByName(target)[0];

		if (documentResult) {
			return documentResult;
		}

		// `getElementById` and `getElementsByName` won't pierce through the shadow DOM so we
		// have to traverse the DOM manually and do the lookup through the shadow roots.
		if (
			typeof document.createTreeWalker === 'function' &&
			document.body &&
			((document.body as any).createShadowRoot || document.body.attachShadow)
		) {
			const treeWalker = document.createTreeWalker(document.body, NodeFilter.SHOW_ELEMENT);
			let currentNode = treeWalker.currentNode as HTMLElement | null;

			while (currentNode) {
				const shadowRoot = currentNode.shadowRoot;

				if (shadowRoot) {
					// Note that `ShadowRoot` doesn't support `getElementsByName`
					// so we have to fall back to `querySelector`.
					const result = shadowRoot.getElementById(target) || shadowRoot.querySelector(`[name="${target}"]`);
					if (result) {
						return result;
					}
				}

				currentNode = treeWalker.nextNode() as HTMLElement | null;
			}
		}

		return null;
	}
}
